import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { Snackbar } from '#src/atoms/snackbar/Snackbar/index.tsx';
import { BrandM, LabelM } from '#src/atoms/typography/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { TextField } from '#src/components/form/Form/index.tsx';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { useLocalStorage } from '#src/hooks/useLocalStorage.ts';
import { glossary } from '#src/lib/glossary.ts';

const DialogWrapper = styled(Vertical).attrs({ gap: 4 })`
  padding: 0 var(--quadruple-unit) var(--quadruple-unit);
`;

type PromoCodeConfig = {
  code: string;
  referrer: string;
  utmCampaign: string;
};

export const EnterPromoCodeButton = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState<string>();
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [, setSearchParams] = useSearchParams();
  const [utmCampaign] = useLocalStorage('UTM_CAMPAIGN', undefined);
  const { formatMessage } = useIntl();
  const {
    flags: { promoCodesConfig },
  } = useFeatureFlags();

  const promoCodes: PromoCodeConfig[] = promoCodesConfig?.promoCodes ?? [];

  const onConfirm = (e: React.FormEvent<HTMLFormElement>) => {
    // avoid page reload
    e.preventDefault();
    // avoid nickname form submission
    // https://github.com/facebook/react/issues/19637
    e.stopPropagation();
    if (!inputValue.trim()) {
      return;
    }
    const promoCodeConfig = promoCodes.find(
      promoCode => promoCode.code === inputValue.trim()
    );
    if (promoCodeConfig) {
      setError(undefined);
      setDialogOpen(false);
      setInputValue('');
      setSuccessSnackbarOpen(true);

      setSearchParams(
        prev => {
          prev.set('utm_campaign', promoCodeConfig.utmCampaign);
          prev.set('referrer', promoCodeConfig.referrer);
          prev.set('action', 'signup');
          return prev;
        },
        {
          replace: true,
        }
      );
    } else {
      setError(
        formatMessage({
          id: 'EnterPromoCodeButton.error.invalidCode',
          defaultMessage: 'Sorry, this code is incorrect or expired',
        })
      );
    }
  };

  return (
    <>
      {!utmCampaign && (
        <Button
          size="small"
          color="transparent"
          startIcon={<FontAwesomeIcon icon={faPlus} />}
          onClick={() => setDialogOpen(true)}
        >
          <FormattedMessage
            id="EnterPromoCodeButton.label"
            defaultMessage="Add a promo code"
          />
        </Button>
      )}
      <Dialog
        open={dialogOpen}
        fullWidth={false}
        maxWidth="md"
        fullScreen={false}
        onClose={() => {
          setDialogOpen(false);
          setInputValue('');
          setError(undefined);
        }}
      >
        <DialogWrapper>
          <BrandM>
            <FormattedMessage
              id="EnterPromoCodeButton.dialogTitle"
              defaultMessage="Enter a valid promo code"
            />
          </BrandM>
          <form onSubmit={onConfirm}>
            <Vertical gap={2}>
              <TextField
                autoFocus
                label={
                  <FormattedMessage
                    id="EnterPromoCodeButton.inputLabel"
                    defaultMessage="Code"
                  />
                }
                value={inputValue}
                onChange={e => {
                  setError(undefined);
                  setInputValue(e.target.value.toUpperCase());
                }}
                name="promoCode"
              />
              {!!error && <LabelM color="var(--c-red-400)">{error}</LabelM>}
              <Button
                color="primary"
                size="medium"
                type="submit"
                disabled={!inputValue.trim()}
              >
                <FormattedMessage {...glossary.confirm} />
              </Button>
            </Vertical>
          </form>
        </DialogWrapper>
      </Dialog>
      <Snackbar
        open={successSnackbarOpen}
        onClose={() => setSuccessSnackbarOpen(false)}
        message={
          <FormattedMessage
            id="EnterPromoCodeButton.codeActivated"
            defaultMessage="Code activated"
          />
        }
      />
    </>
  );
};
